import {
  IframeMessageSchema,
  type AuthTokenPayload,
  type IframeMessage,
  type IframeMessageListener,
} from "@/types"

export type IframeMessageType = (typeof FRAME_MESSAGE_TYPES)[number];

type ExtractMessage<T extends IframeMessageType> = Extract<
  IframeMessage,
  { type: T }
>;

const FRAME_MESSAGE_TYPES = [
  "AUTH_TOKEN",
  "AUTH_TOKEN_REQUEST",
  "RESIZE",
  "ERROR",
  "READY",
  "SET_VISITOR",
  "CLEAR_VISITOR",
] as const

/**
 * IframeMessenger handles secure communication between the chat iframe
 * and the parent webapp using postMessage protocol.
 */
export class IframeMessenger {
  private listeners: Map<string, Set<IframeMessageListener>>
  private isInitialized: boolean = false

  constructor() {
    this.listeners = new Map()
  }

  /**
   * Initialize the messenger and start listening for messages
   */
  init(): void {
    if (this.isInitialized) return

    window.addEventListener("message", this.handleMessage)
    this.isInitialized = true

    // Send ready message to parent
    this.sendReady()
  }

  /**
   * Clean up event listeners
   */
  destroy(): void {
    window.removeEventListener("message", this.handleMessage)
    this.listeners.clear()
    this.isInitialized = false
  }

  /**
   * Handle incoming messages with origin validation and schema validation
   */
  private handleMessage = (event: MessageEvent): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const type = (event.data as any)?.type
    if (FRAME_MESSAGE_TYPES.indexOf(type) === -1) {
      // Ignore unknown message types
      console.debug("Ignoring unknown message type", type)
      return
    }

    // Validate message schema
    const result = IframeMessageSchema.safeParse(event.data)
    if (!result.success) {
      // other messages may come through from other sources, so just log debug info
      // it's not an error we need to warn about
      console.debug("Received invalid message format:", result.error)
      return
    }

    const message = result.data

    // Only process messages from parent
    if (message.source !== "parent") return

    // Notify all listeners for this message type
    this.notifyListeners(message.type, message)
    // Also notify catch-all listeners
    this.notifyListeners("*", message)
  }

  /**
   * Notify listeners for a specific message type
   */
  private notifyListeners(type: string, message: IframeMessage): void {
    const typeListeners = this.listeners.get(type)

    if (typeListeners) {
      typeListeners.forEach((listener) => {
        try {
          listener(message)
        } catch (error) {
          console.error("Error in iframe message listener:", error)
        }
      })
    }
  }

  /**
   * Subscribe to messages of a specific type
   */
  on<T extends IframeMessageType>(
    type: T,
    listener: (message: ExtractMessage<T>) => void,
  ): () => void;
  on(
    type: "*",
    listener: IframeMessageListener,
  ): () => void;
  on(
    type: IframeMessageType | "*",
    listener: IframeMessageListener,
  ): () => void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, new Set())
    }
    this.listeners.get(type)!.add(listener)

    // Return unsubscribe function
    return () => {
      this.listeners.get(type)?.delete(listener)
    }
  }

  /**
   * Send a message to the parent window.
   */
  private sendToParent(message: {
    type: IframeMessageType;
    payload?: unknown;
  }): void {
    if (!window.parent || window.parent === window) {
      console.warn("Not running in iframe, cannot send message to parent")
      return
    }

    const fullMessage = {
      ...message,
      source: "child",
      timestamp: Date.now(),
    }

    window.parent.postMessage(fullMessage, "*")
  }

  /**
   * Send ready message to parent
   */
  sendReady(): void {
    this.sendToParent({ type: "READY" })
  }

  /**
   * Request auth token from parent
   */
  requestAuthToken(): void {
    this.sendToParent({ type: "AUTH_TOKEN_REQUEST" })
  }

  /**
   * Send resize notification to parent
   */
  sendResize(height: number, width?: number): void {
    this.sendToParent({
      type: "RESIZE",
      payload: { height, width },
    })
  }

  /**
   * Send error to parent
   */
  sendError(code: string, message: string): void {
    this.sendToParent({
      type: "ERROR",
      payload: { code, message },
    })
  }

  /**
   * Wait for auth token with timeout
   */
  waitForAuthToken(timeoutMs: number = 5000): Promise<AuthTokenPayload> {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe()
        reject(new Error("Auth token request timed out"))
      }, timeoutMs)

      const unsubscribe = this.on("AUTH_TOKEN", (message) => {
        clearTimeout(timeout)
        unsubscribe()
        resolve(message.payload)
      })

      this.requestAuthToken()
    })
  }
}

// Export singleton instance
export const iframeMessenger = new IframeMessenger()
